import { CAMERA_MODES } from "@uzverse/core";
import type { CameraMode } from "@uzverse/core";

/** The modes this client implements (the platform may define more; capabilities list them). */
export const IMPLEMENTED_MODES: readonly CameraMode[] = [
  "orbit",
  "first_person",
  "third_person",
  "fly",
  "blueprint",
];

/** Keyboard 1..5 in the order the switcher shows them. */
export const MODE_KEYS: Readonly<Record<string, CameraMode>> = {
  "1": "orbit",
  "2": "first_person",
  "3": "third_person",
  "4": "fly",
  "5": "blueprint",
};

export const MODE_ORDER: readonly CameraMode[] = IMPLEMENTED_MODES;

export const isCameraMode = (v: unknown): v is CameraMode =>
  typeof v === "string" && (CAMERA_MODES as readonly string[]).includes(v);

/** Modes offered to the user: what the platform lists (when known) and the client implements. */
export function availableModes(listed: readonly CameraMode[] | null | undefined): CameraMode[] {
  const offered = IMPLEMENTED_MODES.filter(
    (m) => !listed || listed.length === 0 || listed.includes(m),
  );
  return offered.length > 0 ? [...offered] : ["orbit"];
}

/** Modes that move the player through the world with the keyboard (continuous frame loop). */
export const isWalkMode = (m: CameraMode): boolean => m === "first_person" || m === "third_person";
/** Modes that need frames every tick (movement, look). The others render on demand. */
export const isLiveMode = (m: CameraMode): boolean => isWalkMode(m) || m === "fly";

/**
 * Lens per camera mode (vertical degrees). The orbit view is a calm, narrow 40 (the uzpanel plaza
 * camera: less perspective distortion on the towers); the walking and flying views stay wide enough
 * to see where the feet go.
 */
export const CAMERA_FOV = { orbit: 40, first_person: 70, third_person: 55, fly: 55 } as const;
export const fovFor = (mode: CameraMode): number =>
  (CAMERA_FOV as Record<string, number>)[mode] ?? CAMERA_FOV.orbit;

/** Orbit feel: damping factor, polar limits outdoors, and the length of a camera flight (ms). */
export const ORBIT_FEEL = {
  damping: 0.12,
  minPolar: 0.18 * Math.PI,
  maxPolar: 0.48 * Math.PI,
  flightMs: 1000,
} as const;

/** Ease-in-out (cubic) for camera flights. */
export const easeInOut = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export const MODE_STORAGE_KEY = "uz.world.camera.mode";

/** The remembered mode (per viewer, best effort). */
export function loadMode(storage: Pick<Storage, "getItem"> | null = safeStorage()): CameraMode {
  try {
    const v = storage?.getItem(MODE_STORAGE_KEY);
    if (isCameraMode(v) && IMPLEMENTED_MODES.includes(v)) return v;
  } catch {
    // Blocked storage: fall through.
  }
  return "orbit";
}

/** The remembered mode only when one was stored (`loadMode` falls back to orbit). */
export function storedMode(
  storage: Pick<Storage, "getItem"> | null = safeStorage(),
): CameraMode | undefined {
  try {
    const v = storage?.getItem(MODE_STORAGE_KEY);
    return isCameraMode(v) && IMPLEMENTED_MODES.includes(v) ? v : undefined;
  } catch {
    return undefined;
  }
}

export function saveMode(
  mode: CameraMode,
  storage: Pick<Storage, "setItem"> | null = safeStorage(),
): void {
  try {
    storage?.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // Not persisted without storage.
  }
}

export function safeStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}
